import configData from '../configure';

/**
 * Campus File System API — Frontend Integration Service
 * Base path: /campus-file-system/file
 *
 * Rules:
 * 1. Raw JWT token in Authorization header — NO "Bearer " prefix!
 * 2. Step 1: POST /upload (or PUT /{fileId}/content for replace) to declare intent & receive presigned S3 uploadUrl.
 * 3. Step 2: PUT uploadUrl directly to S3 with matching Content-Type. Never log or display S3 XML error body.
 * 4. Step 3: GET /{fileId} poll until ACTIVE (for upload) or pendingStatus disappears (for replace).
 */

const BASE_URL = configData.CAMPUS_FILE_SYSTEM_FILE || 'https://org1.egurukulapps.com/campusresources/api/campus-file-system/file';

/**
 * Extract raw JWT token without 'Bearer ' prefix
 */
export function getRawToken(token) {
    let t = token || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    if (typeof t === 'string' && t.startsWith('Bearer ')) {
        t = t.substring(7).trim();
    }
    return t ? t.trim() : '';
}

/**
 * Step 1: Declare Upload Intent
 * POST /campus-file-system/file/upload
 */
export async function declareFileUpload({ entityType, entityId, orgId, category, filename, contentType, size, uploadedBy, documentType, remarks, description, token }) {
    const payload = {
        entityType: entityType || 'USER', // Allowed: USER, CLASS, COURSE, ASSESSMENT
        entityId: String(entityId || orgId || 'general'),
        orgId: String(orgId || 'DEFAULT'),
        category: category || 'OTHER', // Allowed: OTHER, ASSIGNMENT, STUDY_MATERIAL
        uploadedBy: String(uploadedBy || 'system'),
        filename: filename,
        contentType: contentType || 'application/octet-stream',
        size: Number(size || 0),
    };
    if (documentType) payload.documentType = documentType;
    if (remarks) payload.remarks = remarks;
    if (description) payload.description = description;

    const targetUrl = `${BASE_URL}/upload`;
    const headers = {
        'Authorization': getRawToken(token), // Raw JWT token, NO "Bearer " prefix!
        'Content-Type': 'application/json',
    };

    const response = await fetch(targetUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        let errorMsg = 'Failed to declare file upload intent.';
        try {
            const errData = await response.json();
            errorMsg = errData.message || errorMsg;
        } catch (_) {}
        throw new Error(errorMsg);
    }

    return await response.json(); // { fileId, uploadUrl }
}

/**
 * Step 2: Upload Bytes directly to S3
 * PUT uploadUrl
 */
export async function uploadBytesToS3(uploadUrl, file) {
    const contentType = file.type || 'application/octet-stream';
    const s3Res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': contentType,
        },
        body: file, // Pass File object directly
    });

    if (!s3Res.ok) {
        // DO NOT read/display s3Res.text() XML error response for security reasons
        throw new Error('Upload failed. Please try again.');
    }
}

/**
 * Step 3: Poll File until Resolved
 * GET /campus-file-system/file/{fileId}
 */
export async function pollFileUntilResolved(fileId, token, attempts = 15, delayMs = 2000) {
    for (let i = 0; i < attempts; i++) {
        const res = await fetch(`${BASE_URL}/${fileId}`, {
            headers: {
                'Authorization': token, // Raw JWT token, NO "Bearer " prefix!
            },
        });

        if (res.ok) {
            const record = await res.json();
            if (record.status === 'ACTIVE') return record;
            if (record.status === 'REJECTED') {
                throw new Error(record.rejectionReason || 'File rejected by security validation.');
            }
        }
        await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    throw new Error('Validation is taking longer than expected');
}

/**
 * Complete 3-Step Upload Flow
 */
export async function uploadFileToCampusFileSystem(file, options) {
    const { entityType, entityId, orgId, category, uploadedBy, documentType, remarks, description, token } = options;

    // Step 1: Declare Intent
    const { fileId, uploadUrl } = await declareFileUpload({
        entityType,
        entityId,
        orgId,
        category,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        uploadedBy,
        documentType,
        remarks,
        description,
        token,
    });

    // Step 2: Upload Bytes to S3
    await uploadBytesToS3(uploadUrl, file);

    // Step 3: Poll until ACTIVE
    return await pollFileUntilResolved(fileId, token);
}

/**
 * GET /{fileId} — Get file metadata + on-demand presigned downloadUrl
 */
export async function getFileById(fileId, token, generateDownloadUrl = true) {
    const rawToken = getRawToken(token);
    const res = await fetch(`${BASE_URL}/${fileId}?generateDownloadUrl=${generateDownloadUrl}`, {
        headers: {
            'Authorization': rawToken, // Raw JWT token, NO "Bearer " prefix!
        },
    });

    if (!res.ok) {
        throw new Error('Failed to fetch file metadata.');
    }
    return await res.json();
}

/**
 * GET /entity/{entityId} — List files belonging to an entity (No download URLs generated upfront)
 */
export async function getFilesByEntity(entityId, token, page = 0, pageSize = 50) {
    const rawToken = getRawToken(token);
    const res = await fetch(`${BASE_URL}/entity/${entityId}?page=${page}&pageSize=${pageSize}&generateDownloadUrls=false`, {
        headers: {
            'Authorization': rawToken, // Raw JWT token, NO "Bearer " prefix!
        },
    });

    if (!res.ok) {
        throw new Error('Failed to list entity files.');
    }
    return await res.json();
}

/**
 * PUT /{fileId} — Update metadata only
 */
export async function updateFileMetadata(fileId, metadata, token) {
    const rawToken = getRawToken(token);
    const res = await fetch(`${BASE_URL}/${fileId}`, {
        method: 'PUT',
        headers: {
            'Authorization': rawToken, // Raw JWT token, NO "Bearer " prefix!
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadata),
    });

    if (!res.ok) {
        throw new Error('Failed to update file metadata.');
    }
    return await res.json();
}

/**
 * PUT /{fileId}/content — Replace file content (3-step flow)
 */
export async function replaceFileContent(fileId, file, options) {
    const { uploadedBy, token } = options;
    const rawToken = getRawToken(token);

    // Step 1: Declare Replace Intent
    const declareRes = await fetch(`${BASE_URL}/${fileId}/content`, {
        method: 'PUT',
        headers: {
            'Authorization': rawToken,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
            uploadedBy: uploadedBy || 'system',
        }),
    });

    if (!declareRes.ok) {
        let errorMsg = 'Failed to declare file replace intent.';
        try {
            const errData = await declareRes.json();
            errorMsg = errData.message || errorMsg;
        } catch (_) {}
        throw new Error(errorMsg);
    }

    const { uploadUrl } = await declareRes.json();

    // Step 2: Upload new bytes to S3
    await uploadBytesToS3(uploadUrl, file);

    // Step 3: Poll for replace completion
    for (let i = 0; i < 15; i++) {
        const record = await getFileById(fileId, rawToken);
        if (!record.pendingStatus) return record; // pendingStatus disappears on success
        if (record.pendingStatus === 'REJECTED') {
            throw new Error(record.rejectionReason || 'Replacement file content rejected.');
        }
        await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error('File replacement is taking longer than expected.');
}

/**
 * DELETE /{fileId} — Permanently delete file
 */
export async function deleteFile(fileId, token) {
    const rawToken = getRawToken(token);
    const res = await fetch(`${BASE_URL}/${fileId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': rawToken, // Raw JWT token, NO "Bearer " prefix!
        },
    });

    // Treat 204 or 404 (already deleted) as success
    if (res.status === 204 || res.status === 404 || res.ok) {
        return true;
    }
    throw new Error('Failed to delete file.');
}

/**
 * Universal Download Utility with on-demand S3 Presigned URL, Masked In-Memory Blob & Fallback
 */
export async function downloadCampusFile(fileId, filename, token) {
    if (!fileId) throw new Error("Invalid file ID.");
    const displayName = filename || "Document";
    const rawToken = getRawToken(token);

    // 1. Fetch fresh on-demand presigned S3 URL
    const freshDoc = await getFileById(fileId, rawToken, true);
    const rawUrl = freshDoc?.downloadUrl || freshDoc?.documentUrl || freshDoc?.url;

    if (!rawUrl) {
        throw new Error("Download link is not available for this file.");
    }

    // 2. Try in-memory masked Blob download
    try {
        const response = await fetch(rawUrl);
        if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = displayName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
            return true;
        }
    } catch (blobErr) {
        console.warn("Direct blob download failed (CORS/Network), falling back to direct anchor:", blobErr);
    }

    // 3. Fallback: Browser download trigger via temporary anchor
    const fallbackLink = document.createElement('a');
    fallbackLink.href = rawUrl;
    fallbackLink.target = '_blank';
    fallbackLink.rel = 'noopener noreferrer';
    fallbackLink.download = displayName;
    document.body.appendChild(fallbackLink);
    fallbackLink.click();
    document.body.removeChild(fallbackLink);
    return true;
}
